import { ChangeEvent, FocusEvent, useState, memo } from "react";

import {
  ButtonComponent,
  MajorButtonComponent,
  TextFieldComponent,
} from "@zenith/components";
import EditIcon from "@zenith/assets/icons/EditIcon";
import { useUserDetailsContext } from "@zenith/context/global-context/hooks";
import {
  DEFAULT_COUNTRY_CODE,
  FLAG_ICON,
  PHONE_NUMBER_PREFIX,
} from "@zenith/data/constants";
import useAppNavigation from "@zenith/hooks/appNavigation";
import { SETTINGS } from "@zenith/routes/paths";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { UserDetailsManagementRequestDTO } from "@zenith/services/user";
import { getFormattedDate } from "@zenith/utils";
import {
  ErrorField,
  MerchantProfileForm,
  MerchantProfileFormValidation,
  MerchantProfileFields,
} from "../../utils/form-fields";
import {
  NO_ERROR,
  merchantFormHasError,
  hasMerchantFormChanged,
  isMerchantOTPRequired,
  preventSpace,
  processValue,
  validateMerchantEmail,
  validatePhoneNumber,
} from "../../validations/form-field-validations";

/* eslint-disable-next-line */
export interface MerchantProfileProps {
  updateProfile: (body: UserDetailsManagementRequestDTO) => void;
  updateProfileWithOTP: (body: UserDetailsManagementRequestDTO) => void;
  userFilledData: UserDetailsManagementRequestDTO | null;
}

const MerchantProfileComponent = memo(function MerchantProfileComponent({
  updateProfile,
  updateProfileWithOTP,
  userFilledData,
}: MerchantProfileProps) {
  const { userDetailsContext } = useUserDetailsContext();

  const navigation = useAppNavigation();
  const initialFormData = {
    firstName: userDetailsContext.firstName,
    lastName: userDetailsContext.lastName,
    bvn: userDetailsContext.bankVerificationNumber,
    dateOfBirth: getFormattedDate(userDetailsContext.birthdate),
    account: userDetailsContext.walletId,
    businessName: userDetailsContext.businessName,
    phoneNumber: userDetailsContext.phoneNumber?.substring(4) ?? "",
    phoneNumberPrefix: userDetailsContext.phoneNumber?.substring(0, 4) ?? "",
    email: userDetailsContext.email,
  };

  const [formData, setFormData] = useState<MerchantProfileForm>({
    ...initialFormData,
    ...(userFilledData && {
      ...userFilledData,
      phoneNumber: userFilledData.phoneNumber?.substring(4) ?? "",
    }),
  });

  const hasEmailChanged =
    formData[MerchantProfileFields.EMAIL] !== initialFormData.email;
  const hasPhoneChanged =
    formData[MerchantProfileFields.PHONE_NUMBER] !==
    initialFormData.phoneNumber;

  const hasOnlyOneFieldChanged =
    (hasEmailChanged || hasPhoneChanged) &&
    !(hasEmailChanged && hasPhoneChanged);

  const [isPhoneEditing, setIsPhoneEditing] = useState<boolean>(false);
  const [isEmailEditing, setIsEmailEditing] = useState<boolean>(false);

  const [formValidations, setFormValidations] =
    useState<MerchantProfileFormValidation>({
      [MerchantProfileFields.EMAIL]: formData[MerchantProfileFields.EMAIL]
        ? validateMerchantEmail(
            formData[MerchantProfileFields.EMAIL],
            userDetailsContext.email
          )
        : NO_ERROR,
      [MerchantProfileFields.PHONE_NUMBER]: validatePhoneNumber(
        formData[MerchantProfileFields.PHONE_NUMBER]
      ),
    });

  const onFieldChange = (field: MerchantProfileFields) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const newVal = event.target.value;

      setFormData({
        ...formData,
        [field]: processValue(field, newVal),
      });
      setFormValidations({
        ...formValidations,
        [field]: NO_ERROR,
      });
    };
  };

  const onFieldBlur = (
    field: keyof MerchantProfileForm,
    validate: (value: string) => ErrorField
  ) => {
    return (_: FocusEvent<HTMLInputElement>) => {
      const value = formData[field];

      return setFormValidations({
        ...formValidations,
        [field]: validate(value),
      });
    };
  };

  const onEmailFieldBlur = (
    validate: (value: string, userEmail: string) => ErrorField
  ) => {
    return (_: FocusEvent<HTMLInputElement>) => {
      const value = formData[MerchantProfileFields.EMAIL];
      return setFormValidations({
        ...formValidations,
        [MerchantProfileFields.EMAIL]: validate(
          value,
          userDetailsContext.email
        ),
      });
    };
  };

  const backToSettings = () => {
    navigation.navigateTo(SETTINGS);
  };

  const handleFormSubmit = () => {
    const payload = buildRequestBody();

    if (
      merchantFormHasError(formValidations) ||
      !hasMerchantFormChanged(formData, initialFormData)
    ) {
      return;
    }

    if (payload) {
      if (isMerchantOTPRequired(formData, initialFormData)) {
        updateProfileWithOTP(payload);
        return;
      }
      updateProfile(payload);
    }
  };

  const buildRequestBody = (): UserDetailsManagementRequestDTO | null => {
    const email = formData[MerchantProfileFields.EMAIL];
    const phoneNumber = formData[
      MerchantProfileFields.PHONE_NUMBER_PREFIX
    ].concat(formData[MerchantProfileFields.PHONE_NUMBER]);

    const isEmailRequired = !!userDetailsContext.email.length;

    if (phoneNumber && (!isEmailRequired || email)) {
      return {
        ...(email ? { email } : {}),
        phoneNumber,
      };
    } else {
      return null;
    }
  };

  return (
    <form>
      <div className="flex w-full gap-4">
        <div className="min-w-0 flex-1">
          <TextFieldComponent
            id="first-name"
            label="First Name"
            labelId="label-first-name"
            type="text"
            placeholder=""
            onChange={() => void 0}
            disabled={true}
            value={formData[MerchantProfileFields.FIRST_NAME]}
          />
        </div>
        <div className="min-w-0 flex-1">
          <TextFieldComponent
            id="last-name"
            label="Last Name"
            labelId="label-last-name"
            type="text"
            placeholder=""
            onChange={() => void 0}
            disabled={true}
            value={formData[MerchantProfileFields.LAST_NAME]}
          />
        </div>
      </div>
      <div className="mt-4">
        <TextFieldComponent
          id="bvn"
          label="Bank Verification Number (BVN)"
          labelId="label-bvn"
          type="text"
          placeholder=""
          onChange={() => void 0}
          disabled={true}
          value={formData[MerchantProfileFields.BVN]}
        />
      </div>
      <div className="mt-4">
        <TextFieldComponent
          id="date-of-birth"
          label="Date of Birth"
          labelId="label-date-of-birth"
          type="text"
          placeholder=""
          onChange={() => void 0}
          disabled={true}
          value={formData[MerchantProfileFields.DATE_OF_BIRTH]}
        />
      </div>
      {userDetailsContext.walletId && (
        <div className="mt-4">
          <TextFieldComponent
            id="zenith-account"
            label="Zenith Account"
            labelId="label-zenith-account"
            type="text"
            placeholder=""
            onChange={() => void 0}
            disabled={true}
            value={userDetailsContext.walletId}
          />
        </div>
      )}
      {formData[MerchantProfileFields.BUSINESS_NAME] && (
        <div className="mt-4">
          <TextFieldComponent
            id="business-name"
            label="Business Name"
            labelId="label-business-name"
            type="text"
            placeholder=""
            onChange={() => void 0}
            disabled={true}
            value={formData[MerchantProfileFields.BUSINESS_NAME]}
          />
        </div>
      )}
      <div className="mt-4 flex w-full gap-1 md:gap-4">
        <div className="w-1/4 md:w-auto">
          <label
            id="label-phone-prefix"
            className="label block w-full whitespace-nowrap"
          >
            Phone Number
          </label>
          <div
            id="phone-prefix"
            className="text-field select-none bg-neutral-25"
          >
            <div className="align-center flex justify-center p-3 text-neutral-400">
              <FLAG_ICON />{" "}
              {formData[MerchantProfileFields.PHONE_NUMBER_PREFIX] ||
                PHONE_NUMBER_PREFIX}
            </div>
          </div>
        </div>
        <div className="flex grow items-end gap-2 pt-6 sm:gap-4">
          <div className="w-full shrink-0">
            <TextFieldComponent
              id="phone-number"
              label=""
              labelId="label-phone-number"
              type="tel"
              maxLength={getPhoneNumberLength(
                DEFAULT_COUNTRY_CODE,
                formData[MerchantProfileFields.PHONE_NUMBER]
              )}
              placeholder="Enter phone number"
              onChange={onFieldChange(MerchantProfileFields.PHONE_NUMBER)}
              value={formData[MerchantProfileFields.PHONE_NUMBER]}
              disabled={!isPhoneEditing}
              onBlur={onFieldBlur(
                MerchantProfileFields.PHONE_NUMBER,
                validatePhoneNumber
              )}
              hasError={
                formValidations[MerchantProfileFields.PHONE_NUMBER]?.hasError
              }
            />
            {formValidations[MerchantProfileFields.PHONE_NUMBER]?.hasError && (
              <div className="mt-1 ml-2 text-xs text-red-400">
                <span className="break-words">
                  {formValidations[MerchantProfileFields.PHONE_NUMBER].message}
                </span>
              </div>
            )}
          </div>
          {formData[MerchantProfileFields.PHONE_NUMBER] && !isPhoneEditing && (
            <button
              type="button"
              className="mb-2 flex shrink-0 items-center text-neutral-500 hover:text-neutral-800"
              onClick={() => setIsPhoneEditing(true)}
            >
              <EditIcon />
            </button>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-end gap-2 sm:gap-4">
        <div className="w-full shrink-0">
          <TextFieldComponent
            id="email-address"
            label={
              userDetailsContext.email.length
                ? "Email Address"
                : "Email Address (optional)"
            }
            labelId="label-email-address"
            type="email"
            placeholder="Enter email address"
            onChange={onFieldChange(MerchantProfileFields.EMAIL)}
            onKeyDown={preventSpace}
            value={formData[MerchantProfileFields.EMAIL]}
            disabled={!isEmailEditing && userDetailsContext.email.length > 0}
            onBlur={onEmailFieldBlur(validateMerchantEmail)}
            hasError={formValidations[MerchantProfileFields.EMAIL]?.hasError}
          />
        </div>
        {formData[MerchantProfileFields.EMAIL] && !isEmailEditing && (
          <button
            type="button"
            className="mb-2 flex shrink-0 items-center text-neutral-500 hover:text-neutral-800"
            onClick={() => setIsEmailEditing(true)}
          >
            <EditIcon />
          </button>
        )}
      </div>
      {formValidations[MerchantProfileFields.EMAIL]?.hasError && (
        <div className="mt-1 ml-2 text-xs text-red-400">
          <span className="break-words">
            {formValidations[MerchantProfileFields.EMAIL].message}
          </span>
        </div>
      )}

      <div className="mt-20 mb-12 flex gap-2">
        <div className=" w-full">
          <ButtonComponent
            id="cancel-button"
            isLoading={false}
            type="secondary"
            fullWidth
            disabled={false}
            onClick={backToSettings}
            size="large"
            label="Cancel"
          />
        </div>
        <div className=" w-full">
          <ButtonComponent
            id="save-changes-button"
            isLoading={false}
            type="primary"
            fullWidth
            disabled={
              merchantFormHasError(formValidations) || !hasOnlyOneFieldChanged
            }
            size="large"
            onClick={handleFormSubmit}
            label="Save Changes"
          />
        </div>
      </div>
    </form>
  );
});

export default MerchantProfileComponent;
